import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import Webcam from 'react-webcam';
import Cropper from 'react-easy-crop';
import { Modal, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  FaArrowLeft,
  FaUser,
  FaPhone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaIdBadge,
  FaCheckCircle,
  FaTimesCircle,
  FaKey,
  FaBook,
  FaClipboard,
  FaUserShield,
  FaAddressCard,
  FaEdit,
  FaCoins,
  FaCamera,
  FaUpload,
  FaCut,
  FaRedo,
  FaUsers,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentView = () => {
  const { id } = useParams();
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showModal, setShowModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('user');

  useEffect(() => {
    if (id) {
      fetchStudent(id); // Pass id explicitly
    } else {
      setError('Invalid Student ID.');
      setLoading(false);
    }
  }, [id, selectedAcademicYear]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && student) {
      const studentData = {
        ...student, // Keep all existing student data
        photo_path: student.photo
          ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
          : 'default.png', // Fallback photo
      };

      setTimeout(() => {
        iframe.contentWindow.postMessage(studentData, '*');
      }, 500); // Delay ensures iframe is ready
    }
  }, [student, showModal]);

  const toggleCamera = () => {
    setFacingMode((prevMode) => (prevMode === 'user' ? 'environment' : 'user'));
  };

  const fetchStudent = async (studentId) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setError('Please select an academic year.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-v2`,
        {
          id: studentId,
          academic_year_id: selectedAcademicYear.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.status === 'success' && response.data.data) {
        setStudent(response.data.data);
      } else {
        setError('Student not found.');
      }
    } catch (err) {
      setError('Failed to fetch student details.');
    }
    setLoading(false);
  };

  const handleAdmit = async () => {
    if (!window.confirm('Are you sure you want to admit this student?')) return;

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}admit-student`,
        { application_no: student.application_no },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: `Student admitted! Admission No: ${response.data.admission_no}`,
          variant: 'success',
        });
        setTimeout(
          () => setAlert({ show: false, message: '', variant: '' }),
          3000,
        );
        setStudent({ ...student, admission_no: response.data.admission_no });
        fetchStudent(student.id);
      } else {
        setAlert({
          show: true,
          message: response.data.message,
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Error admitting student.',
        variant: 'danger',
      });
    }
  };

  const handlePhotoClose = () => {
    setShowPhotoModal(false);
    setImage(null);
    setCroppedImage(null);
    setCroppedAreaPixels(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const capture = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImage(imageSrc);
    } else {
      console.error('Webcam not initialized.');
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const cropImage = async () => {
    if (!image || !croppedAreaPixels) return;

    const img = new Image();
    img.src = image;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        img,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
      );

      setCroppedImage(canvas.toDataURL('image/jpeg'));
    };
  };

  const uploadImage = async (studentId) => {
    if (!croppedImage) return;

    try {
      const token = getAuthToken();
      const blob = await fetch(croppedImage).then((res) => res.blob());

      const formData = new FormData();
      formData.append('id', studentId);
      formData.append('photo', blob, 'student_photo.jpg');

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}upload-photo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: 'Photo uploaded successfully!',
          variant: 'success',
        });
        setTimeout(
          () => setAlert({ show: false, message: '', variant: '' }),
          3000,
        );
        fetchStudent(student.id);
        handlePhotoClose();
      } else {
        setAlert({
          show: true,
          message: `Upload Failed: ${response.data.message}`,
          variant: 'danger',
        });
      }
    } catch (err) {
      console.error('Upload Error:', err);
      setAlert({
        show: true,
        message: 'An error occurred while uploading the photo.',
        variant: 'danger',
      });
    }
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Unified Student Details Card with Title Bar and Content in ONE box */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Compact Title Bar inside main box */}
          <div
            style={{
              background: '#2d3a4e',
              color: 'white',
              padding: '18px 30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              fontWeight: '600',
              fontSize: '1.35rem',
              minHeight: '56px',
            }}
          >
            <span
              style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
            >
              <FaUser style={{ fontSize: '1.5rem' }} />
              {student?.name || 'Student'}
            </span>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {student && (
                <>
                  <Button
                    variant="light"
                    onClick={() => setShowPhotoModal(true)}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaCamera /> Photo
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => setShowModal(true)}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaIdBadge /> ID Card
                  </Button>
                  <Button
                    variant="warning"
                    onClick={() => navigate(`/edit-student/${student.id}`)}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaEdit /> Edit
                  </Button>

                  {student.admission_no ? (
                    <Button
                      variant="info"
                      onClick={() =>
                        navigate(`/new-fee-management/${student.id}`)
                      }
                      style={{
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <FaCoins /> Fees
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleAdmit}
                      style={{
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontWeight: '600',
                      }}
                    >
                      Admit Student
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="outline-light"
                onClick={() => navigate(-1)}
                style={{
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaArrowLeft /> Back
              </Button>
            </div>
          </div>

          {/* Alert Messages */}
          {alert.show && (
            <Alert
              variant={alert.variant}
              dismissible
              onClose={() => setAlert({ ...alert, show: false })}
              style={{
                borderRadius: '8px',
                margin: '20px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              {alert.message}
            </Alert>
          )}

          {/* Main Content (no extra box) */}
          <div style={{ padding: '30px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spinner animation="border" variant="primary" />
                <p style={{ marginTop: '15px', color: '#6c757d' }}>
                  Loading student details...
                </p>
              </div>
            ) : error ? (
              <Alert variant="danger" style={{ borderRadius: '8px' }}>
                {error}
              </Alert>
            ) : !student ? (
              <Alert variant="warning" style={{ borderRadius: '8px' }}>
                Student not found.
              </Alert>
            ) : (
              <>
                {/* Top Section - Photo and Basic Info */}

                {/* Alert Messages */}
                {alert.show && (
                  <Alert
                    variant={alert.variant}
                    dismissible
                    onClose={() => setAlert({ ...alert, show: false })}
                    style={{
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  >
                    {alert.message}
                  </Alert>
                )}
                {/* Student Details Card */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '30px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                >
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Spinner animation="border" variant="primary" />
                      <p style={{ marginTop: '15px', color: '#6c757d' }}>
                        Loading student details...
                      </p>
                    </div>
                  ) : error ? (
                    <Alert variant="danger" style={{ borderRadius: '8px' }}>
                      {error}
                    </Alert>
                  ) : !student ? (
                    <Alert variant="warning" style={{ borderRadius: '8px' }}>
                      Student not found.
                    </Alert>
                  ) : (
                    <>
                      {/* Top Section - Photo and Basic Info */}
                      <div className="row mb-4">
                        <div className="col-12 col-md-3 text-center mb-4 mb-md-0">
                          <div
                            style={{
                              position: 'relative',
                              display: 'inline-block',
                            }}
                          >
                            <img
                              src={
                                student.photo
                                  ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
                                  : `${appConstants.API_ROOT_URL}uploads/students/blank.png`
                              }
                              alt="Student"
                              style={{
                                width: '180px',
                                height: '180px',
                                objectFit: 'cover',
                                borderRadius: '12px',
                                border: '3px solid #2d3a4e',
                                boxShadow: '0 4px 12px rgba(45, 58, 78, 0.18)',
                              }}
                            />
                          </div>
                          <h4
                            style={{
                              marginTop: '15px',
                              fontWeight: '700',
                              color: '#333',
                            }}
                          >
                            {student.name}
                          </h4>
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: '0.9rem',
                              padding: '3px 12px',
                              borderRadius: '4px',
                              backgroundColor:
                                student.gender === 'Male'
                                  ? '#0d6efd'
                                  : student.gender === 'Female'
                                    ? '#ff69b4'
                                    : '#6c757d',
                              color: 'white',
                              fontWeight: '500',
                            }}
                          >
                            {student.gender}
                          </span>
                          {student.admission_no && (
                            <div style={{ marginTop: '10px' }}>
                              <Badge
                                bg="success"
                                style={{
                                  fontSize: '0.9rem',
                                  padding: '6px 12px',
                                }}
                              >
                                <FaCheckCircle style={{ marginRight: '5px' }} />
                                Admitted
                              </Badge>
                            </div>
                          )}
                        </div>

                        <div className="col-12 col-md-9">
                          <div className="row g-3">
                            <div className="col-12 col-md-6">
                              <div
                                style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaIdBadge
                                    style={{
                                      color: '#2d3a4e',
                                      fontSize: '1.1rem',
                                    }}
                                  />
                                  <strong style={{ color: '#495057' }}>
                                    Application No:
                                  </strong>
                                </div>
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {student.application_no}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div
                                style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaCalendarAlt
                                    style={{
                                      color: '#2d3a4e',
                                      fontSize: '1.1rem',
                                    }}
                                  />
                                  <strong style={{ color: '#495057' }}>
                                    Applied On:
                                  </strong>
                                </div>
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {student.date_of_application}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div
                                style={{
                                  padding: '12px',
                                  backgroundColor: student.admission_no
                                    ? '#d4edda'
                                    : '#f8f9fa',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaIdBadge
                                    style={{
                                      color: student.admission_no
                                        ? '#28a745'
                                        : '#2d3a4e',
                                      fontSize: '1.1rem',
                                    }}
                                  />
                                  <strong style={{ color: '#495057' }}>
                                    Admission No:
                                  </strong>
                                </div>
                                <div
                                  style={{
                                    fontWeight: student.admission_no
                                      ? '600'
                                      : '400',
                                    color: student.admission_no
                                      ? '#28a745'
                                      : '#6c757d',
                                    fontSize: '1.1rem',
                                  }}
                                >
                                  {student.admission_no || 'Not Admitted Yet'}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div
                                style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaCalendarAlt
                                    style={{
                                      color: '#667eea',
                                      fontSize: '1.1rem',
                                    }}
                                  />
                                  <strong style={{ color: '#495057' }}>
                                    Admitted On:
                                  </strong>
                                </div>
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {student.date_of_admission || 'N/A'}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div
                                style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaPhone
                                    style={{
                                      color: '#667eea',
                                      fontSize: '1.1rem',
                                    }}
                                  />
                                  <strong style={{ color: '#495057' }}>
                                    Mobile:
                                  </strong>
                                </div>
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {student.mobile || 'N/A'}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div
                                style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaMapMarkerAlt
                                    style={{
                                      color: '#667eea',
                                      fontSize: '1.1rem',
                                    }}
                                  />
                                  <strong style={{ color: '#495057' }}>
                                    Address:
                                  </strong>
                                </div>
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {student.address || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Information Cards */}
                      <div className="row g-4">
                        {/* Personal Info */}
                        <div className="col-12 col-md-4">
                          <div
                            style={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              height: '100%',
                            }}
                          >
                            <div
                              style={{
                                background: '#2d3a4e',
                                color: 'white',
                                padding: '15px 20px',
                                fontWeight: '600',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                              }}
                            >
                              <FaUser /> Personal Info
                            </div>
                            <div style={{ padding: '20px' }}>
                              <div style={{ marginBottom: '15px' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#6c757d',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaCalendarAlt />
                                  <strong>Date of Birth:</strong>
                                </div>
                                <div style={{ paddingLeft: '28px' }}>
                                  {student.dob || 'N/A'}
                                </div>
                              </div>
                              <div style={{ marginBottom: '15px' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#6c757d',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaUsers />
                                  <strong>Parents/Guardian:</strong>
                                </div>
                                <div style={{ paddingLeft: '28px' }}>
                                  {student.parents || 'N/A'}
                                </div>
                              </div>
                              <div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#6c757d',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaClipboard />
                                  <strong>Remark:</strong>
                                </div>
                                <div style={{ paddingLeft: '28px' }}>
                                  {student.remark || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Academic Info */}
                        <div className="col-12 col-md-4">
                          <div
                            style={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              height: '100%',
                            }}
                          >
                            <div
                              style={{
                                background: '#2d3a4e',
                                color: 'white',
                                padding: '15px 20px',
                                fontWeight: '600',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                              }}
                            >
                              <FaBook /> Academic Info
                            </div>
                            <div style={{ padding: '20px' }}>
                              <div style={{ marginBottom: '15px' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#6c757d',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaKey />
                                  <strong>Parent Access Code:</strong>
                                </div>
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    fontFamily: 'monospace',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#667eea',
                                  }}
                                >
                                  {student.parent_access_code || 'N/A'}
                                </div>
                              </div>
                              <div style={{ marginBottom: '15px' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#6c757d',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaAddressCard />
                                  <strong>ID Card No:</strong>
                                </div>
                                <div style={{ paddingLeft: '28px' }}>
                                  {student.id_card_no || 'N/A'}
                                </div>
                              </div>
                              <div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#6c757d',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <FaCheckCircle />
                                  <strong>Status:</strong>
                                </div>
                                <div style={{ paddingLeft: '28px' }}>
                                  <Badge
                                    bg={
                                      student.status === 'Active'
                                        ? 'success'
                                        : 'danger'
                                    }
                                    style={{ fontSize: '0.9rem' }}
                                  >
                                    {student.status}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Batch Info */}
                        <div className="col-12 col-md-4">
                          <div
                            style={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              height: '100%',
                            }}
                          >
                            <div
                              style={{
                                background: '#2d3a4e',
                                color: 'white',
                                padding: '15px 20px',
                                fontWeight: '600',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                              }}
                            >
                              <FaBook /> Batch Information
                            </div>
                            <div style={{ padding: '20px' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  color: '#6c757d',
                                  marginBottom: '10px',
                                }}
                              >
                                <FaBook />
                                <strong>Assigned Batches:</strong>
                              </div>
                              {student.batches && student.batches.length > 0 ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    paddingLeft: '28px',
                                  }}
                                >
                                  {student.batches.map((b, i) => (
                                    <Badge
                                      key={i}
                                      bg="info"
                                      text="dark"
                                      style={{
                                        fontSize: '0.85rem',
                                        padding: '6px 12px',
                                      }}
                                    >
                                      {b.class_name} - {b.division}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    paddingLeft: '28px',
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  No batches assigned
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ID Card Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderBottom: 'none',
          }}
        >
          <Modal.Title
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <FaIdBadge /> Student ID Card
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <iframe
            ref={iframeRef}
            src="/instv2/idcard/index.html?v=1"
            style={{
              width: '100%',
              height: '500px',
              border: 'none',
              borderRadius: '8px',
            }}
            title="ID Card"
          />
        </Modal.Body>
      </Modal>

      {/* Photo Upload Modal */}
      <Modal show={showPhotoModal} onHide={handlePhotoClose} centered>
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderBottom: 'none',
          }}
        >
          <Modal.Title
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <FaCamera /> Take Photo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px', textAlign: 'center' }}>
          {!image ? (
            <>
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-100"
                videoConstraints={{ facingMode }}
                style={{ borderRadius: '8px' }}
              />
              <div className="row g-2 mt-3">
                <div className="col-6">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={capture}
                    style={{
                      borderRadius: '8px',
                      padding: '12px',
                      fontWeight: '600',
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                    }}
                  >
                    <FaCamera className="me-2" /> Capture
                  </Button>
                </div>
                <div className="col-6">
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      borderRadius: '8px',
                      padding: '12px',
                      fontWeight: '600',
                    }}
                  >
                    <FaUpload className="me-2" /> Browse
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </>
          ) : !croppedImage ? (
            <>
              <div
                className="crop-container"
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid={true}
                />
              </div>
              <Button
                variant="success"
                className="w-100 mt-3"
                onClick={cropImage}
                style={{
                  borderRadius: '8px',
                  padding: '12px',
                  fontWeight: '600',
                }}
              >
                <FaCut className="me-2" /> Crop Image
              </Button>
            </>
          ) : (
            <>
              <img
                src={croppedImage}
                className="w-100"
                alt="Cropped"
                style={{ borderRadius: '8px', border: '2px solid #667eea' }}
              />
              <Button
                variant="success"
                className="w-100 mt-3"
                onClick={() => uploadImage(student.id)}
                style={{
                  borderRadius: '8px',
                  padding: '12px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                }}
              >
                <FaUpload className="me-2" /> Upload Photo
              </Button>
            </>
          )}
          <Button
            variant="warning"
            className="w-100 mt-2"
            onClick={() => setImage(null)}
            style={{ borderRadius: '8px', padding: '10px', fontWeight: '600' }}
          >
            <FaRedo className="me-2" /> Retake Photo
          </Button>
          <Button
            variant="info"
            className="w-100 mt-2"
            onClick={toggleCamera}
            style={{ borderRadius: '8px', padding: '10px', fontWeight: '600' }}
          >
            <FaRedo className="me-2" /> Switch Camera
          </Button>
        </Modal.Body>
      </Modal>
    </Layout>
  );
};

export default StudentView;
